/**
 * GlossaryHelp — a lightweight, area-scoped definitions dialog.
 *
 * Each page shows ONLY its own terminology (no cross-page bleed):
 *   • area="operations" → Operations tabs (Business & Revenue, Growth &
 *     Trials, System & Flags, AI Cost & Usage).
 *   • area="governance"  → Governance terms (audit/access/compliance).
 * Reports uses its own existing hub glossary drawer (not this component).
 *
 * This reuses the existing lightweight glossary mechanism — no new system.
 */
import React from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth-context";

// ── Operations: grouped by the four Operations tabs ──────────────────────────
const OPERATIONS_GROUPS = [
    {
        heading: "Business & Revenue",
        terms: [
            ["MRR", "Monthly Recurring Revenue — predictable subscription revenue recognised each month."],
            ["ARR", "Annual Recurring Revenue — MRR × 12."],
            ["Active Subscriptions", "The count of currently active (paying) subscriptions."],
            ["Plan", "The subscription tier assigned to a tenant (e.g. Standard, Pro) that sets entitlements and pricing."],
            ["Effective $/mo", "The actual monthly charge for a tenant after any plan overrides or discounts."],
        ],
    },
    {
        heading: "Growth & Trials",
        terms: [
            ["Trial", "A time-limited free period before a tenant converts to a paid plan."],
            ["Active Trial", "A trial that is currently running (not yet expired or converted)."],
            ["Trial Conversion", "The share of trials that become paying subscriptions."],
            ["Trial Expiry", "The date a trial ends if it is not converted."],
        ],
    },
    {
        heading: "System & Flags",
        terms: [
            ["Feature Flag", "A toggle that turns a platform capability on or off without a deploy."],
            ["System Health", "Live status of core services and dependencies."],
            ["Pause / Resume", "Temporarily halt or restart platform activity (e.g. scheduled jobs) from the control panel."],
        ],
    },
    {
        heading: "AI Cost & Usage",
        terms: [
            ["AI Cost", "The money ($) spent on LLM provider calls — tokens used × the provider's price."],
            ["AI Usage", "The volume of AI activity — number of queries/calls and tokens processed."],
            ["LLM", "Large Language Model — the AI model that powers Sense AI features."],
            ["Provider", "The company serving the model (e.g. OpenAI, Anthropic, Google)."],
            ["Model", "The specific LLM version used for a request."],
            ["Credits", "The plan's AI allowance unit consumed by each AI request."],
            ["Tokens In", "Tokens sent to the model on a request (prompt + context)."],
            ["Tokens Out", "Tokens the model generated in its response."],
            ["Cost", "The USD cost attributed to an AI call or group of calls."],
            ["Avg Credits/Query", "Average credits consumed per AI query over the selected window."],
            ["Latency", "How long an AI call took to complete, in milliseconds (ms)."],
            ["Anomaly", "A call or pattern flagged as unusually costly/slow versus the norm."],
        ],
    },
];

// ── Governance: audit / access / compliance ─────────────────────────────────
const GOVERNANCE_GROUPS = [
    {
        heading: "Access & Identity",
        terms: [
            ["RBAC", "Role-Based Access Control — the rules that decide what each role may see and do."],
            ["Privileged Access", "Accounts with elevated (admin-level) permissions that warrant extra review."],
            ["User Access Review", "A periodic audit of who has access to what, for compliance sign-off."],
            ["Deprovisioning", "Removing a user's access when they leave or change roles."],
        ],
    },
    {
        heading: "Security & Compliance",
        terms: [
            ["Audit Event", "A recorded system action (login, permission change, data access) retained for compliance."],
            ["Authentication / Login Events", "A log of sign-in attempts and sessions."],
            ["RBAC Change History", "A trail of changes made to roles and permissions over time."],
        ],
    },
    {
        heading: "Usage & Commercial",
        terms: [
            ["Usage Summary", "A governance view of platform usage for oversight."],
            ["Scheduled Jobs", "Background/automated tasks and their run status."],
            ["Connector Add-on Spend", "The monthly cost of paid connector add-ons beyond the plan's included set."],
        ],
    },
];

const AREA_CONFIG = {
    operations: {
        title: "Operations glossary",
        description: "Definitions for the terms and metrics shown on the Operations tabs.",
        groups: OPERATIONS_GROUPS,
    },
    governance: {
        title: "Governance glossary",
        description: "Definitions for the audit, access and compliance terms shown on Governance.",
        groups: GOVERNANCE_GROUPS,
    },
};

// Platform revenue terms are Super-Admin only.
const SA_ONLY_TERMS = new Set(["MRR", "ARR"]);

export default function GlossaryHelp({ area = "operations", label = "Definitions", testId = "glossary-help" }) {
    const { user } = useAuth();
    const isSa = user?.role === "super_admin";
    const base = AREA_CONFIG[area] || AREA_CONFIG.operations;
    const cfg = isSa ? base : {
        ...base,
        groups: base.groups.map((g) => ({ ...g, terms: g.terms.filter(([t]) => !SA_ONLY_TERMS.has(t)) })),
    };
    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" data-testid={`${testId}-trigger`}>
                    <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
                    <span className="text-xs">{label}</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
                <DialogHeader>
                    <DialogTitle>{cfg.title}</DialogTitle>
                    <DialogDescription>{cfg.description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-1">
                    {cfg.groups.map((g) => (
                        <section key={g.heading} data-testid={`${testId}-group-${g.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                            <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{g.heading}</h3>
                            <dl className="space-y-1.5">
                                {g.terms.map(([term, def]) => (
                                    <div key={term} className="grid grid-cols-[150px_1fr] gap-2 text-sm items-start">
                                        <dt className="font-medium">{term}</dt>
                                        <dd className="text-muted-foreground">{def}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
